import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, ModalBody } from 'reactstrap';
import Swipe from 'react-easy-swipe';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import { requestMedia } from '../../actions/actions';
import Photo from '../../containers/photoConnect';
import MediaOverlayButton from './MediaOverlayButton';
import { CloseIcon } from '../Icons';

class MediaOverlay extends React.Component {
  constructor(props) {
    super(props);

    this.onClose = this.onClose.bind(this);
    this.goToPrevious = this.goToPrevious.bind(this);
    this.goToNext = this.goToNext.bind(this);
    this.resetScrollPosition = this.resetScrollPosition.bind(this);
    this.modalBodyRef = React.createRef();

    this.state = {
      swipeDirection: null,
      expectedMediaId: null,
    };
  }

  componentDidUpdate(prevProps) {
    // Clear expectedMediaId when the overlay first opens
    if (!prevProps.overlayOpen && this.props.overlayOpen) {
      this.setState({ expectedMediaId: null });
      return;
    }

    // Clear expectedMediaId once the expected media has arrived in Redux
    if (this.state.expectedMediaId && this.props.activeMedia?.id === this.state.expectedMediaId) {
      this.setState({ expectedMediaId: null });
    }
  }

  onClose(e) {
    this.props.onClose();
  }

  /**
   * Reset scroll position to top of the modal body.
   * This ensures proper scroll behavior when opening the modal or navigating between captures.
   */
  resetScrollPosition() {
    if (this.modalBodyRef.current) {
      this.modalBodyRef.current.scrollTop = 0;
    }
    // Also reset scroll on the modal-body element if it exists
    const modalBody = document.querySelector('.event-overlay .modal-body');
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
  }

  goToPrevious() {
    this.setState({ swipeDirection: 'left', expectedMediaId: this.props.prevId });
    this.props.setActiveMedia(this.props.prevId);
    // Reset scroll when navigating to a new capture
    setTimeout(this.resetScrollPosition, 0);
  }

  goToNext() {
    this.setState({ swipeDirection: 'right', expectedMediaId: this.props.nextId });
    this.props.setActiveMedia(this.props.nextId);
    // Reset scroll when navigating to a new capture
    setTimeout(this.resetScrollPosition, 0);
  }

  render() {
    const isNavigating = !!this.state.expectedMediaId;

    return (
      <Modal
        isOpen={this.props.overlayOpen}
        toggle={this.onClose}
        centered={true}
        fade={false}
        className="event-overlay fullscreen-overlay"
        backdropClassName="event-overlay-backdrop"
        onOpened={this.resetScrollPosition}
      >
        <div className="event-overlay__toolbar" style={{ backgroundColor: this.props.accentColor }}>
          <button className="event-overlay__close" onClick={this.onClose} style={{ color: this.props.contrastColor }}>
            Close
            <CloseIcon color={this.props.contrastColor} />
          </button>
        </div>

        <MediaOverlayButton
          direction="prev"
          id={this.props.prevId}
          handleClick={this.goToPrevious}
          color={this.props.contrastColor}
          buildingItems={isNavigating || this.props.buildingItems}
        />

        <ModalBody innerRef={this.modalBodyRef}>
          {this.props.activeMedia && this.props.eventId && (
            <Swipe
              className="event-overlay__swiper"
              onSwipeLeft={this.goToNext}
              onSwipeRight={this.goToPrevious}
              tolerance={100}
            >
              <Photo
                eventId={this.props.eventId}
                photoId={this.props.activeMedia.id}
                beta={this.props.beta}
                isServer={this.props.isServer}
                popup={true}
                audioEnabled={this.props.audioEnabled}
                captureMode={this.props.captureMode}
              />
            </Swipe>
          )}
        </ModalBody>

        <MediaOverlayButton
          direction="next"
          id={this.props.nextId}
          handleClick={this.goToNext}
          color={this.props.contrastColor}
          buildingItems={isNavigating || this.props.buildingItems}
        />
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const event = state.activeEvent;
  // Use eventId from ownProps if provided (for gallery pages), otherwise from activeEvent
  const eventId = ownProps.eventId || (event && event.eventId ? event.eventId : null);

  return {
    eventId,
    activeMedia: state.activeMedia,
    photos: state.photos,
    photoIndex: eventId ? state.events[eventId]?.eventMedias?.map((m) => m.mediaId) ?? [] : [],
    accentColor: event && event.accentColor ? event.accentColor : '#000',
    contrastColor: event && event.contrastColor ? event.contrastColor : '#fff',
    beta: state.beta,
    isServer: state.isServer,
  };
};

export default connect(mapStateToProps)(MediaOverlay);
